//@ts-check
import {DEFAULT_TABLE_PAGE_SIZE} from '@/utils/constants/commonConstants'
import {useCallback, useMemo, useRef, useState} from 'react'

/** @typedef {{column: string, direction: 'asc' | 'desc'}} SortEntry */
/** @typedef {{field: string, type: string, value?: string, values?: string[], operator?: string}} SearchFilter */

/**
 * Generic state hook for v2 DataTable — manages pagination, sorting, column
 * filters and global search. Exposes callbacks that can be passed directly to
 * the DataTable component props.
 *
 * @param {{ defaultPageSize?: number; debounceMs?: number }} [options]
 */
export const useDataTableV2 = ({defaultPageSize = DEFAULT_TABLE_PAGE_SIZE, debounceMs = 500} = {}) => {
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(defaultPageSize)
  const [sorting, setSorting] = useState(/** @type {SortEntry | null} */ (null))
  const [columnFilters, setColumnFilters] = useState(/** @type {Record<string, SearchFilter>} */ ({}))

  const debounceTimers = useRef(/** @type {Record<string, ReturnType<typeof setTimeout>>} */ ({}))

  /** @type {(newPage: number) => void} */
  const onPageIndexChange = useCallback(
    newPage => setPageIndex(newPage - 1), // DataTable sends 1-based, API expects 0-based
    []
  )

  /** @type {(newPageSize: number) => void} */
  const onRowsPerPageChange = useCallback(newPageSize => {
    setPageSize(newPageSize)
    setPageIndex(0)
  }, [])

  /** @param {SortEntry[]} sortingState */
  const sortingHandler = useCallback(sortingState => {
    setSorting(sortingState?.[0] ?? null)
    setPageIndex(0)
  }, [])

  /**
   * @param {unknown} value
   * @param {'text' | 'dropdown' | 'date' | 'dateRange' | undefined} type
   * @param {string} columnKey
   */
  const onColumnFiltersChange = useCallback(
    (value, type, columnKey) => {
      debugger
      clearTimeout(debounceTimers.current[columnKey])
      debounceTimers.current[columnKey] = setTimeout(() => {
        setColumnFilters(prev => {
          const next = {...prev}
          if (value === '' || value === null || value === undefined) {
            delete next[columnKey]
          } else {
            next[columnKey] = {field: columnKey, value: String(value), type: 'string'}
          }
          return next
        })
        setPageIndex(0)
      }, debounceMs)
    },
    [debounceMs]
  )

  /** @param {string} value */
  const onGlobalSearchChange = useCallback(
    value => {
      clearTimeout(debounceTimers.current['__global__'])
      debounceTimers.current['__global__'] = setTimeout(() => {
        setColumnFilters(prev => {
          const next = {...prev}
          if (!value) {
            delete next['__global__']
          } else {
            next['__global__'] = {field: 'globalSearch', value, type: 'string'}
          }
          return next
        })
        setPageIndex(0)
      }, debounceMs)
    },
    [debounceMs]
  )

  const handleResetAll = useCallback(() => {
    setColumnFilters({})
    setSorting(null)
    setPageIndex(0)
  }, [])

  /** @type {SearchFilter[]} */
  const activeFilters = useMemo(() => Object.values(columnFilters), [columnFilters])

  return {
    // state
    pageIndex,
    pageSize,
    sorting,
    activeFilters,
    // DataTable callbacks
    onPageIndexChange,
    onRowsPerPageChange,
    sortingHandler,
    onColumnFiltersChange,
    onGlobalSearchChange,
    handleResetAll,
  }
}
